import type { FC, FormEvent, ReactNode } from 'react'
import type { Cart, MenuCategory, MenuItem, PlacedOrder } from '@/types'
import type { PickupDay } from '@/lib/hours'

import {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import {
    ChevronDown,
    CircleAlert,
    CircleCheck,
    LoaderCircle,
    Minus,
    Phone,
    Plus,
    ShoppingBag,
    Trash2
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { formatPrice, fromCents, toCents } from '@/lib/format'
import { formatPickup, formatTime, getPickupDays } from '@/lib/hours'
import { Button, buttonClasses } from '@/components/generals/button'
import { MAX_QUANTITY_PER_ITEM } from '@/constants/order'
import { siteConfig } from '@/constants/site'

interface OrderFormProps {
    categories: Array<MenuCategory>
    cart: Cart
    onQuantityChange: (itemId: string, quantity: number) => void
    onClear: () => void
}

interface FieldProps {
    id: string
    label: string
    error?: string
    optional?: boolean
    children: ReactNode
}

type FieldName = 'items' | 'pickup' | 'name' | 'phone' | 'email'

type FieldErrors = Partial<Record<FieldName, string>>

type CartLine = {
    item: MenuItem
    quantity: number
}

const FIELD_IDS: Record<FieldName, string> = {
    items: 'order-add-item',
    pickup: 'order-day',
    name: 'order-name',
    phone: 'order-phone',
    email: 'order-email'
}

const inputClasses = 'h-11 w-full rounded-xl border border-input bg-background px-3.5 text-base text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground/70 focus:border-primary focus:ring-4 focus:ring-primary/15 disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus:ring-destructive/15 sm:text-sm'

const selectClasses = cn(inputClasses, 'appearance-none pr-10')

const Field: FC<FieldProps> = ({ id, label, error, optional = false, children }): ReactNode => {
    return (
        <div>
            <label htmlFor={id} className='flex items-baseline justify-between gap-2 text-sm font-semibold'>
                {label}

                {optional && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        Optional
                    </span>
                )}
            </label>

            <div className='relative mt-1.5'>
                {children}
            </div>

            {error && (
                <p id={`${id}-error`} className='mt-1.5 flex items-center gap-1.5 text-sm text-destructive'>
                    <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            )}
        </div>
    )
}

const SelectChevron: FC = (): ReactNode => (
    <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
)

export const OrderForm: FC<OrderFormProps> = ({ categories, cart, onQuantityChange, onClear }): ReactNode => {
    const [pickupDays, setPickupDays] = useState<Array<PickupDay>>([])
    const [hasLoadedDays, setHasLoadedDays] = useState(false)
    const [pickupDate, setPickupDate] = useState('')
    const [pickupTime, setPickupTime] = useState('')
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [notes, setNotes] = useState('')
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<FieldErrors>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [placedOrder, setPlacedOrder] = useState<PlacedOrder | null>(null)
    const [soldOutIds, setSoldOutIds] = useState<Array<string>>([])
    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    // Pickup days depend on the visitor's clock, so they are worked out once the page has loaded.
    useEffect(() => {
        const refresh = () => {
            setPickupDays(getPickupDays(new Date()))
            setHasLoadedDays(true)
        }

        refresh()

        const timer = window.setInterval(refresh, 5 * 60 * 1000)

        return () => window.clearInterval(timer)
    }, [])

    // Keep the chosen day and time valid whenever the list of pickup slots changes.
    useEffect(() => {
        if (pickupDays.length === 0) return

        const day = pickupDays.find(option => option.value === pickupDate) ?? pickupDays[0]

        if (day.value !== pickupDate) setPickupDate(day.value)
        if (!day.slots.includes(pickupTime)) setPickupTime(day.slots[0])
    }, [pickupDays, pickupDate, pickupTime])

    useEffect(() => {
        if (placedOrder) successHeadingRef.current?.focus()
    }, [placedOrder])

    const itemsById = useMemo(() => new Map(categories.flatMap(category => category.items).map(item => [item.id, item] as const)), [categories])

    const lines: Array<CartLine> = Object.entries(cart)
        .map(([id, quantity]) => ({ item: itemsById.get(id), quantity }))
        .filter((line): line is CartLine => Boolean(line.item) && line.quantity > 0)

    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0)
    const total = fromCents(lines.reduce((sum, line) => sum + toCents(line.item.price) * line.quantity, 0))
    const selectedDay = pickupDays.find(option => option.value === pickupDate)
    const itemsError = lines.length === 0 ? errors.items : undefined

    const clearError = (field: FieldName) => {
        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const addItem = (itemId: string) => {
        if (!itemId) return

        onQuantityChange(itemId, Math.min(MAX_QUANTITY_PER_ITEM, (cart[itemId] ?? 0) + 1))
        clearError('items')
    }

    const validate = (): FieldErrors => {
        const next: FieldErrors = {}

        if (lines.length === 0) next.items = "Add at least one bowl, side or drink."
        if (!pickupDate || !pickupTime) next.pickup = "Choose a day and time to pick up."
        if (name.trim().length < 2) next.name = "Please add your name."
        if (phone.replace(/\D/g, '').length < 7) next.phone = "Please add a phone number we can reach you on."
        if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) next.email = "That email address doesn't look quite right."

        return next
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (isSubmitting) return

        const nextErrors = validate()
        const firstInvalid = (Object.keys(FIELD_IDS) as Array<FieldName>).find(field => nextErrors[field])

        setErrors(nextErrors)
        setFormError(null)
        setSoldOutIds([])

        if (firstInvalid) {
            document.getElementById(FIELD_IDS[firstInvalid])?.focus()

            return
        }

        setIsSubmitting(true)

        try {
            const response = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: lines.map(line => ({ id: line.item.id, quantity: line.quantity })),
                    pickupDate,
                    pickupTime,
                    name,
                    phone,
                    email,
                    notes,
                    website
                })
            })

            const data = await response.json().catch(() => null) as { order?: PlacedOrder, message?: string, soldOut?: Array<string> } | null

            if (!response.ok || !data?.order) {
                setFormError(data?.message ?? `We couldn't place your order. Please try again or call us at ${siteConfig.phone}.`)
                const soldOut = data?.soldOut

                setSoldOutIds(Array.isArray(soldOut) ? soldOut : [])

                return
            }

            setPlacedOrder(data.order)
            setNotes('')
            onClear()
        }
        catch {
            setFormError(`We couldn't reach the kitchen. Check your connection and try again, or call us at ${siteConfig.phone}.`)
        }
        finally {
            setIsSubmitting(false)
        }
    }

    if (placedOrder) {
        return (
            <div role='status' className='text-center'>
                <span className='mx-auto inline-flex size-14 items-center justify-center rounded-full bg-scallion-soft text-scallion'>
                    <CircleCheck className='size-7' aria-hidden='true' />
                </span>

                <h3
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className='mt-5 text-3xl font-semibold outline-none'
                >
                    Your order is in!
                </h3>

                <p className='mt-2 text-muted-foreground'>
                    Order number{' '}
                    <strong className='font-mono text-base text-foreground'>
                        {placedOrder.orderNumber}
                    </strong>
                </p>

                <div className='mt-7 rounded-2xl bg-muted/70 p-5 text-left'>
                    <p className='text-sm text-muted-foreground'>
                        Ready for pickup
                    </p>

                    <p className='font-heading text-xl font-semibold'>
                        {formatPickup(placedOrder.pickupDate, placedOrder.pickupTime)}
                    </p>

                    <ul className='mt-4 divide-y divide-border/80 border-t border-border/80 text-sm'>
                        {placedOrder.lines.map(line => (
                            <li key={line.id} className='flex items-baseline justify-between gap-4 py-2.5'>
                                <span>
                                    <span className='tabular-nums text-muted-foreground'>{line.quantity} x </span>
                                    <span data-mdlfy-cms={`menu_items:${line.id}:name`}>{line.name}</span>
                                </span>

                                <span className='tabular-nums'>
                                    {formatPrice(fromCents(toCents(line.price) * line.quantity))}
                                </span>
                            </li>
                        ))}
                    </ul>

                    <p className='flex items-baseline justify-between border-t border-border/80 pt-3 font-semibold'>
                        <span>Total, pay at pickup</span>
                        <span className='tabular-nums'>{formatPrice(placedOrder.total)}</span>
                    </p>
                </div>

                <p className='mt-6 text-sm leading-relaxed text-muted-foreground'>
                    We will have it hot and packed when you arrive. Something changed? Call us at{' '}
                    <a href={siteConfig.phoneHref} className='font-semibold text-foreground underline underline-offset-4'>
                        {siteConfig.phone}
                    </a>
                    .
                </p>

                <Button
                    variant='outline'
                    size='md'
                    className='mt-6'
                    onClick={() => setPlacedOrder(null)}
                >
                    Start another order
                </Button>
            </div>
        )
    }

    if (categories.length === 0) {
        return (
            <div className='py-6 text-center'>
                <ShoppingBag className='mx-auto size-8 text-muted-foreground' aria-hidden='true' />

                <h3 className='mt-4 text-2xl font-semibold'>
                    Online ordering is taking a short break
                </h3>

                <p className='mx-auto mt-2 max-w-sm text-muted-foreground'>
                    Give us a call and we will put your order in by phone.
                </p>

                <a href={siteConfig.phoneHref} className={buttonClasses('primary', 'md', 'mt-6')}>
                    <Phone />
                    Call {siteConfig.phone}
                </a>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} noValidate aria-label="Pickup order" className='relative'>
            <div className='flex items-center justify-between gap-4'>
                <h3 className='text-2xl font-semibold'>
                    Your order
                </h3>

                {itemCount > 0 && (
                    <span className='rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary'>
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                    </span>
                )}
            </div>

            {lines.length === 0 ? (
                <div className={cn('mt-5 rounded-2xl border border-dashed px-5 py-7 text-center', itemsError ? 'border-destructive/60' : 'border-border')}>
                    <ShoppingBag className='mx-auto size-7 text-muted-foreground' aria-hidden='true' />

                    <p className='mt-3 font-semibold'>
                        Nothing in your order yet
                    </p>

                    <p className='mt-1 text-sm text-muted-foreground'>
                        Add bowls from the menu above, or pick something below.
                    </p>
                </div>
            ) : (
                <ul className='mt-4 divide-y divide-border/80'>
                    {lines.map(({ item, quantity }) => (
                        <li key={item.id} className='flex items-center gap-3 py-3'>
                            <div className='min-w-0 flex-1'>
                                <p
                                    data-mdlfy-cms={`menu_items:${item.id}:name`}
                                    className='font-semibold leading-snug'
                                >
                                    {item.name}
                                </p>

                                <p className='text-sm text-muted-foreground'>
                                    <span className='tabular-nums'>{formatPrice(item.price)}</span> each
                                </p>

                                {soldOutIds.includes(item.id) && (
                                    <p className='mt-1 inline-flex items-center gap-1 text-xs font-semibold text-destructive'>
                                        <CircleAlert className='size-3.5' aria-hidden='true' />
                                        Just sold out, please remove
                                    </p>
                                )}
                            </div>

                            <div role='group' aria-label={`Quantity of ${item.name}`} className='flex items-center rounded-full border border-border bg-background p-0.5'>
                                <button
                                    type='button'
                                    onClick={() => onQuantityChange(item.id, quantity - 1)}
                                    aria-label={quantity === 1 ? `Remove ${item.name}` : `One less ${item.name}`}
                                    className='inline-flex size-8 items-center justify-center rounded-full text-foreground/70 outline-none transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {quantity === 1 ? <Trash2 className='size-4' aria-hidden='true' /> : <Minus className='size-4' aria-hidden='true' />}
                                </button>

                                <span className='w-7 text-center text-sm font-semibold tabular-nums'>
                                    {quantity}
                                </span>

                                <button
                                    type='button'
                                    onClick={() => onQuantityChange(item.id, quantity + 1)}
                                    disabled={quantity >= MAX_QUANTITY_PER_ITEM}
                                    aria-label={`One more ${item.name}`}
                                    className='inline-flex size-8 items-center justify-center rounded-full text-foreground/70 outline-none transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40'
                                >
                                    <Plus className='size-4' aria-hidden='true' />
                                </button>
                            </div>

                            <p className='w-[4.5rem] text-right font-semibold tabular-nums'>
                                {formatPrice(fromCents(toCents(item.price) * quantity))}
                            </p>
                        </li>
                    ))}
                </ul>
            )}

            <div className='mt-4'>
                <label htmlFor='order-add-item' className='sr-only'>
                    Add an item to your order
                </label>

                <div className='relative'>
                    <Plus aria-hidden='true' className='pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-primary' />

                    <select
                        id='order-add-item'
                        value=''
                        onChange={event => addItem(event.target.value)}
                        aria-invalid={Boolean(itemsError)}
                        aria-describedby={itemsError ? 'order-add-item-error' : undefined}
                        className={cn(selectClasses, 'pl-10 font-medium')}
                    >
                        <option value=''>
                            Add a bowl, side or drink
                        </option>

                        {categories.map(category => (
                            <optgroup key={category.id} label={category.name}>
                                {category.items.filter(item => item.isAvailable).map(item => (
                                    <option
                                        key={item.id}
                                        value={item.id}
                                        data-mdlfy-cms={`menu_items:${item.id}:name`}
                                    >
                                        {item.name}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>

                    <SelectChevron />
                </div>

                {itemsError && (
                    <p id='order-add-item-error' className='mt-1.5 flex items-center gap-1.5 text-sm text-destructive'>
                        <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                        {itemsError}
                    </p>
                )}
            </div>

            <div className='mt-5 flex items-baseline justify-between border-t border-border pt-4'>
                <span className='font-semibold'>
                    Total
                </span>

                <span className='text-right'>
                    <span className='block font-heading text-2xl font-semibold tabular-nums'>
                        {formatPrice(total)}
                    </span>

                    <span className='block text-xs text-muted-foreground'>
                        Pay at the counter when you pick up
                    </span>
                </span>
            </div>

            <fieldset className='mt-7'>
                <legend className='text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                    Pickup
                </legend>

                {hasLoadedDays && pickupDays.length === 0 ? (
                    <p className='mt-3 rounded-xl bg-muted px-4 py-3 text-sm'>
                        We have no pickup times left this week. Please call us at {siteConfig.phone}.
                    </p>
                ) : (
                    <div className='mt-3 grid gap-4 sm:grid-cols-2'>
                        <Field id='order-day' label="Day" error={errors.pickup}>
                            <select
                                id='order-day'
                                value={pickupDate}
                                onChange={event => {
                                    setPickupDate(event.target.value)
                                    clearError('pickup')
                                }}
                                disabled={pickupDays.length === 0}
                                aria-invalid={Boolean(errors.pickup)}
                                aria-describedby={errors.pickup ? 'order-day-error' : undefined}
                                className={selectClasses}
                            >
                                {pickupDays.length === 0 && (
                                    <option value=''>
                                        Loading days
                                    </option>
                                )}

                                {pickupDays.map(day => (
                                    <option key={day.value} value={day.value}>
                                        {day.label}
                                    </option>
                                ))}
                            </select>

                            <SelectChevron />
                        </Field>

                        <Field id='order-time' label="Time">
                            <select
                                id='order-time'
                                value={pickupTime}
                                onChange={event => {
                                    setPickupTime(event.target.value)
                                    clearError('pickup')
                                }}
                                disabled={!selectedDay}
                                className={selectClasses}
                            >
                                {!selectedDay && (
                                    <option value=''>
                                        Choose a day first
                                    </option>
                                )}

                                {(selectedDay?.slots ?? []).map(slot => (
                                    <option key={slot} value={slot}>
                                        {formatTime(slot)}
                                    </option>
                                ))}
                            </select>

                            <SelectChevron />
                        </Field>
                    </div>
                )}
            </fieldset>

            <fieldset className='mt-7'>
                <legend className='text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                    Your details
                </legend>

                <div className='mt-3 grid gap-4 sm:grid-cols-2'>
                    <Field id='order-name' label="Name" error={errors.name}>
                        <input
                            id='order-name'
                            type='text'
                            autoComplete='name'
                            value={name}
                            maxLength={80}
                            onChange={event => {
                                setName(event.target.value)
                                clearError('name')
                            }}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? 'order-name-error' : undefined}
                            className={inputClasses}
                        />
                    </Field>

                    <Field id='order-phone' label="Phone" error={errors.phone}>
                        <input
                            id='order-phone'
                            type='tel'
                            autoComplete='tel'
                            inputMode='tel'
                            value={phone}
                            maxLength={30}
                            onChange={event => {
                                setPhone(event.target.value)
                                clearError('phone')
                            }}
                            aria-invalid={Boolean(errors.phone)}
                            aria-describedby={errors.phone ? 'order-phone-error' : undefined}
                            className={inputClasses}
                        />
                    </Field>

                    <div className='sm:col-span-2'>
                        <Field id='order-email' label="Email" optional error={errors.email}>
                            <input
                                id='order-email'
                                type='email'
                                autoComplete='email'
                                value={email}
                                maxLength={120}
                                onChange={event => {
                                    setEmail(event.target.value)
                                    clearError('email')
                                }}
                                aria-invalid={Boolean(errors.email)}
                                aria-describedby={errors.email ? 'order-email-error' : undefined}
                                className={inputClasses}
                            />
                        </Field>
                    </div>

                    <div className='sm:col-span-2'>
                        <Field id='order-notes' label="Notes for the kitchen" optional>
                            <textarea
                                id='order-notes'
                                rows={3}
                                value={notes}
                                maxLength={500}
                                onChange={event => setNotes(event.target.value)}
                                placeholder="Allergies, extra ginger, no scallions..."
                                className={cn(inputClasses, 'h-auto resize-y py-3')}
                            />
                        </Field>
                    </div>
                </div>
            </fieldset>

            <div aria-hidden='true' className='absolute -left-[9999px] top-0 size-px overflow-hidden'>
                <label htmlFor='order-website'>
                    Website
                </label>

                <input
                    id='order-website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={website}
                    onChange={event => setWebsite(event.target.value)}
                />
            </div>

            {formError && (
                <p role='alert' className='mt-6 flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {formError}
                </p>
            )}

            <Button
                type='submit'
                size='lg'
                className='mt-7 w-full'
                disabled={isSubmitting}
            >
                {isSubmitting ? (
                    <>
                        <LoaderCircle className='animate-spin' aria-hidden='true' />
                        Placing your order
                    </>
                ) : (
                    <>
                        Place pickup order
                        {itemCount > 0 && (
                            <span className='font-normal opacity-80'>
                                ({formatPrice(total)})
                            </span>
                        )}
                    </>
                )}
            </Button>

            <p className='mt-3 text-center text-xs text-muted-foreground'>
                No payment now. We only use your details for this order.
            </p>
        </form>
    )
}
